import React from 'react';
import { Platform } from '../util/platform';
import { StyleSheet, Pressable, ActivityIndicator, View } from 'react-native'
import { widthPercentageToDP as wp } from 'react-native-responsive-screen';
import { Feather } from '@expo/vector-icons';

import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';

import CustomText from './CustomText';

/**
 * AppButton - V3 button component with multiple variants
 *
 * @param {string} title - Button text
 * @param {function} onPress - Callback when button is pressed
 * @param {string} variant - Button variant: 'primary' | 'secondary' | 'text'
 * @param {object} buttonStyle - Additional styles for the button
 * @param {object} textStyle - Additional styles for the text
 * @param {boolean} isBusy - Whether to show loading indicator
 * @param {boolean} isEnabled - Whether the button is enabled
 * @param {string} icon - Optional Feather icon name
 * @param {string} iconPosition - Icon position: 'left' | 'right'
 * @param {boolean} fullWidth - Whether button should be full width
 */
export default function AppButton({
  title,
  onPress,
  variant = 'primary',
  buttonStyle = null,
  textStyle = null,
  isBusy = false,
  isEnabled = true,
  icon,
  iconPosition = 'right',
  fullWidth = false,
}) {
  const { isDesktop } = useResponsiveStyles();

  const buttonWidth = fullWidth ? '100%' : isDesktop ? wp(40) : wp(80);
  const fontSize = isDesktop ? 20 : 18;

  const getVariantStyles = () => {
    switch (variant) {
      case 'secondary':
        return {
          button: styles.buttonSecondary,
          text: styles.textSecondary,
          disabledButton: styles.buttonSecondaryDisabled,
          disabledText: styles.textSecondaryDisabled,
        };
      case 'text':
        return {
          button: styles.buttonText,
          text: styles.textLink,
          disabledButton: styles.buttonTextDisabled,
          disabledText: styles.textLinkDisabled,
        };
      default:
        return {
          button: styles.buttonPrimary,
          text: styles.textPrimary,
          disabledButton: styles.buttonPrimaryDisabled,
          disabledText: styles.textPrimaryDisabled,
        };
    }
  };

  const variantStyles = getVariantStyles();
  const textColor = isEnabled
    ? variant === 'primary'
      ? '#000000'
      : AppStyles.colorSet.brandPrimary
    : variant === 'primary'
      ? '#8C8C8C'
      : 'rgba(255, 196, 81, 0.5)';

  const renderIcon = () => {
    if (!icon) return null;
    return (
      <Feather
        name={icon}
        size={fontSize + 4}
        color={textColor}
        style={iconPosition === 'left' ? styles.iconLeft : styles.iconRight}
      />
    );
  };

  return (
    <Pressable
      style={({ pressed, hovered, focused }) => [
        styles.buttonBase,
        variantStyles.button,
        buttonStyle,
        !buttonStyle && { width: buttonWidth },
        !isEnabled && variantStyles.disabledButton,
        hovered && isEnabled && styles.hovered,
        focused && isEnabled && styles.focused,
        pressed && isEnabled && styles.pressed,
      ]}
      onPress={!isBusy ? onPress : undefined}
      accessibilityRole="button"
      accessibilityState={{ disabled: !isEnabled }}
    >
      <View style={styles.content}>
        {iconPosition === 'left' && renderIcon()}
        <CustomText
          font="semiBold"
          size={fontSize}
          color={textColor}
          style={[variantStyles.text, textStyle, !isEnabled && variantStyles.disabledText]}
        >
          {title}
        </CustomText>
        {isBusy ? (
          <ActivityIndicator
            size="small"
            color={textColor}
            style={styles.iconRight}
          />
        ) : (
          iconPosition === 'right' && renderIcon()
        )}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  buttonBase: {
    cursor: 'pointer',
    marginVertical: 10,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Primary variant
  buttonPrimary: {
    backgroundColor: AppStyles.colorSet.brandPrimary,
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  buttonPrimaryDisabled: {
    backgroundColor: 'rgba(255, 196, 81, 0.5)',
  },
  textPrimary: {},
  textPrimaryDisabled: {},
  // Secondary variant
  buttonSecondary: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: AppStyles.colorSet.brandPrimary,
    paddingVertical: 14,
    paddingHorizontal: 24,
  },
  buttonSecondaryDisabled: {
    borderColor: 'rgba(255, 196, 81, 0.5)',
  },
  textSecondary: {},
  textSecondaryDisabled: {},
  // Text variant
  buttonText: {
    backgroundColor: 'transparent',
    paddingVertical: 8,
    paddingHorizontal: 0,
  },
  buttonTextDisabled: {},
  textLink: {
    textDecorationLine: 'underline',
  },
  textLinkDisabled: {},
  // Icons
  iconLeft: {
    marginRight: 8,
  },
  iconRight: {
    marginLeft: 8,
  },
  // States
  hovered: {
    opacity: 0.9,
    // Subtle lift on web pointer hover (ignored on native)
    ...Platform.select({
      web: {
        transitionDuration: '150ms',
        transitionProperty: 'opacity, box-shadow',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
      },
      default: {},
    }),
  },
  // Keyboard focus ring — neutral dark so it stays visible on gold and transparent variants
  focused: Platform.select({
    web: {
      outlineStyle: 'solid',
      outlineWidth: 2,
      outlineColor: 'rgba(17, 24, 39, 0.45)',
      outlineOffset: 2,
    },
    default: {},
  }),
  pressed: {
    opacity: 0.8,
  },
});
